import { useState, Fragment } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from 'src/context/renewal'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData } from 'src/dtos/applicationData'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { TbFileCheck } from 'react-icons/tb'
import AreasOfPractice from 'src/components/application/mainPagesComponents/Firm/AreasOfPractice'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

const Firm = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { renewalData, saveRenewalData, updateRenewalData } = useRenewal()
  const [review, setReview] = useState<ReviewData>(renewalData?.firmSection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!renewalData?.firmSection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!renewalData?.firmSection?.review?.files)

  const areasOfPractice = renewalData?.firmSection?.practiceAreas

  const handleEditClick = () => {
    navigate({
      pathname: '/customer/renew/firm/main',
      search: createSearchParams({ review: 'firm' }).toString()
    })
  }

  const updatedApplication = () => ({
    ...renewalData,
    stoppedAt: pathname,
    firmSection: {
      ...renewalData?.firmSection,
      review
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveRenewalData(updatedApplication())
    } catch(_) {}
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...renewalData,
    stoppedAt: pathname,
    firmSection: {
      ...renewalData?.firmSection,
      review: {
        ...renewalData?.firmSection?.review,
        [key]: value
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveRenewalData(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveRenewalData(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveRenewalData(applicationFileAttached)
    }
  }

  const firmSection = renewalData?.firmSection
  const transactions = renewalData?.firmSection?.securitiesLawInfo?.transactions

  
  const FirmBasicsSection = () => (
    <section>
      <h5>FIRM BASICS</h5>
      <article>
        <strong>Firm Name</strong>
        <span>{firmSection?.firmName}</span>
      </article>
      <article>
        <strong>Type of Practice</strong>
        <span>{firmSection?.practiceType}</span>
      </article>
      {firmSection?.establishmentDate != null ? 
      <article>
      <strong>Firm Established</strong>
      <span>{firmSection?.establishmentDate}</span>
      </article>
      : <div></div>}
      <article>
        <strong>Contact Information</strong>
        <span>{firmSection?.contactInfo?.phoneNumber}</span>
        {firmSection?.contactInfo?.website ?
          <span>{firmSection?.contactInfo?.website}</span> :
          null
        }
        {firmSection?.contactInfo?.faxNumber ?
          <span>{firmSection?.contactInfo?.faxNumber}</span> :
          null
        }
      </article>
      <article>
        <strong>Principal Mailing Address</strong>
        <span>{firmSection?.mailAddress?.formattedAddress}</span>
        <span>{firmSection?.mailAddress?.suiteNumber}</span>
      </article>
      {firmSection?.isBillingEqualToMail === false ? (
        <article>
          <strong>Billing Address</strong>
          <span>{firmSection?.billingAddress?.formattedAddress}</span>
          <span>{firmSection?.billingAddress?.suiteNumber}</span>
        </article>
      ) : null}
       <article>
        <strong>Number of Offices</strong>
        <span>{firmSection?.numberOfOffices ?? 'N/A'}</span>
      </article>
      {firmSection?.isSecondaryContactAdded === true ? (
        <article>
          <strong>Secondary Contact</strong>
          <span>{[firmSection?.secondaryContact?.firstName, firmSection?.secondaryContact?.lastName].join(' ')}</span>
          <span>{firmSection?.secondaryContact?.emailAddress}</span>
          <span>{firmSection?.secondaryContact?.phoneNumber}</span>
          {firmSection?.secondaryContact?.isFirmAdministrator === true ?
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <IoIosCheckmarkCircle /> Firm Administrator
            </span> :
            null
          }
        </article>
      ) : null}
    </section>
  )

  const AOPSection = () => (
    <section>
      {areasOfPractice && areasOfPractice.length > 0 ? (
        <Fragment>
          <h5>AREAS OF PRACTICE</h5>
          <article>
            <AreasOfPractice practiceAreas={areasOfPractice} />
          </article>
        </Fragment>
      ) : null}
    </section>
  )

  const RealEstateSection = () => (
    <section>
      <h5>REAL ESTATE</h5>
      <article>
        <strong>Is any firm member a licensed escrow officer for a title company?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberWithEscrowLicense)}</span>
      </article>
      <article>
        <strong>Does Applicant or any firm member have any agreements with title companies?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies)}</span>
        {firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies === true ? firmSection?.realEstateInfo?.agreementWithTitleCompaniesFile?.map(file =>
          <span key={file?.fileName} style={{flexDirection: 'row', alignItems: 'center'}}><TbFileCheck /> {file?.fileName ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>Is any firm member a fee attorney for a title company?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberFeeAttorney)}</span>
      </article>
      <article>
        <strong>Is any firm member an outside closing attorney, approved attorney or P-22 attorney?</strong>
        <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberOutsideClosingAttorney)}</span>
      </article>
      <article>
        <strong>How many non-attorney licensed escrow officers are currently employed by your firm?</strong>
        <span>{firmSection?.realEstateInfo?.nonAttorneysWithEscrowLicenseQuantity ?? 'N/A'}</span>
      </article>
    </section>
  )

  const SecuritiesSection = () => (
    <section>
      <h5>SECURITIES LAW</h5>
      {firmSection?.securitiesLawInfo?.hasFiles === true ? (
        <>
          <article>
            <strong>
              Uploaded files of securities transactions
            </strong>
            {firmSection?.securitiesLawInfo?.securitiesLawFiles?.map(file => (
              <span key={file.fileName} style={{flexDirection: 'row', alignItems: 'center'}}><TbFileCheck /> {file.fileName}</span>
            ))}
          </article>
        </>
      ) : (
        <>
          {transactions && transactions.length > 0 ? transactions?.map((transaction, index) =>
            <Fragment key={index}>
              <article><span><h6>Transaction {index + 1}</h6></span></article>
              <article>
                <strong>
                  Date
                </strong>
                <span>{transaction?.date ?? 'N/A'}</span>
              </article>
              <article>
                <strong>
                  Client name
                </strong>
                <span>{transaction?.clientName ?? 'N/A'}</span>
              </article>
              <article>
                <strong>
                  Size of offering
                </strong>
                <span>{transaction?.offeringSize ?? 'N/A'}</span>
              </article>
              <article>
                <strong>
                  Role in transaction
                </strong>
                <span>{transaction?.transactionRole ?? 'N/A'}</span>
              </article>
            </Fragment>
          ) : null}
          </>
        )}
    </section>
  )

  const ColumnsBuilder = () => {
    const sectionsArray = [
      <FirmBasicsSection/>,
      <AOPSection/>,
      <RealEstateSection/>,
      <SecuritiesSection/>,
    ]

    const sectionsToRender = sectionsArray
      .map((section, index) => {
        switch(index) {
          case 2:
            return !!firmSection?.practiceAreas?.find(a => a.area === 'Real Estate') ? section : null
          case 3: 
            return !!firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt' || a.area === 'Securities - Other Types Of Securities Practice') ? section : null
          default: return section
        }
      })
      .filter(section => section !== null)

    const leftColumn = sectionsToRender.filter((section, index) => index % 2 === 0)
    const rightColumn = sectionsToRender.filter((section, index) => index % 2 !== 0)

    return (
      <>
        <div className='column'>
          {leftColumn}
        </div>
        <div className='column'>
          {rightColumn}
        </div>
      </>
    )
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your firm information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='firmData'>
          <header>
            <div>
              <h4>Firm</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{padding: '8px 46px'}} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            {ColumnsBuilder()}
          </main>
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!renewalData?.firmSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={10}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/quote/select"
        nextRoute="/customer/renew/review/attorneys"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Firm